// A reader's own text size. Two stores are built from this:
//
//   - the study store, shared by the study workbench (/learn, through
//     components/courses/study/useTextScale.js) and the study-note reader
//     (study_note_reader.js). One key, so a size chosen on one is the size on
//     the other. Plain JS on purpose: the note reader is ERB and must not pull
//     React in.
//   - the exam store (components/mocks/sitting/useDisplaySettings.js), which
//     reaches 200% because that is what the high-stakes computer tests a
//     student is rehearsing for offer (text zoom to 200% and beyond). Its own
//     key: a size a student reads a note at is not a decision about how they
//     sit a paper.
//
// Steps rather than a slider or a free number. A slider invites fiddling
// with a value nobody can name, and every intermediate size has to be checked
// against the layout; each step here has been looked at.
//
// 100% is the DEFAULT, not the floor. The design system's 14px floor governs
// what this app authors — shipping 13px comparison rows is a design failure —
// and a reader choosing smaller text for themselves is the opposite of that.
// See the note in components/_study.scss before removing the 0.9 step.

export const createScaleStore = ({ key, steps, defaultScale = 1 }) => {
  // CLAMPED TO THE STEP LIST, never merely parsed.
  //
  // localStorage is writable by anybody with devtools and the value ends up
  // inside a `calc()`. `Number()` on "1; background: url(...)" gives NaN and
  // is caught, but a bare parse that happened to succeed on something
  // unexpected would put it in the stylesheet — so nothing reaches the DOM
  // that is not one of the known steps.
  const clamp = (value) => {
    const parsed = Number(value);
    return steps.includes(parsed) ? parsed : defaultScale;
  };

  const read = () => {
    try {
      return clamp(window.localStorage.getItem(key));
    } catch (e) {
      // Private windows and blocked site data throw on ACCESS, not on read. A
      // reader who cannot store a preference must still get the page.
      return defaultScale;
    }
  };

  const write = (value) => {
    try {
      window.localStorage.setItem(key, String(value));
    } catch (e) {
      // Nothing to do and nothing to tell them: the size still applied for
      // this visit, which is most of what they wanted.
    }
  };

  // The step `direction` (+1 / -1) away from `current`, or `current` itself
  // at either end. Never wraps: the largest size back to the smallest on one
  // more press of A+ is the kind of surprise only ever discovered by somebody
  // who did not want it.
  const stepFrom = (current, direction) => {
    const next = steps[steps.indexOf(current) + direction];
    return next === undefined ? current : next;
  };

  return { STEPS: steps, DEFAULT_SCALE: defaultScale, clamp, read, write, stepFrom };
};

const study = createScaleStore({ key: 'pq.studyTextScale', steps: [0.9, 1, 1.15, 1.3, 1.45] });

export const { STEPS, DEFAULT_SCALE, clamp, read, write, stepFrom } = study;

export const examScale = createScaleStore({
  key: 'pq.examTextScale',
  steps: [0.9, 1, 1.15, 1.3, 1.5, 1.75, 2],
});
